import React, { createContext, useContext, useState, useEffect } from 'react';
import { getUnreadNotificationCount } from '../services/notificationService';
import { useAuth } from './AuthContext';

const NotificationContext = createContext({});

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!user) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    
    const { data, error } = await getUnreadNotificationCount();
    
    if (!error) {
      setUnreadCount(data || 0);
    } else {
      console.error('Error fetching unread count:', error);
      setUnreadCount(0);
    }
    setLoading(false);
  };

  // Update unread count (can be called from anywhere)
  const updateUnreadCount = (delta) => {
    setUnreadCount(prev => Math.max(0, prev + delta));
  };

  // Set unread count directly
  const setUnreadCountDirectly = (count) => {
    setUnreadCount(count);
  };

  // Initial load and when user changes
  useEffect(() => {
    fetchUnreadCount();
  }, [user]);

  // Poll for updates every 30 seconds
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [user]);

  const value = {
    unreadCount,
    loading,
    updateUnreadCount,
    setUnreadCount: setUnreadCountDirectly,
    refreshUnreadCount: fetchUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};